import { BemHelper } from "@/utils/BemHelper";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import CtButton from "../button/CtButton";
import { Theme, ThemeFontVariant, ThemeIntent } from "../types";
import { THEMES, THEME_FONT_VARIANTS, THEME_INTENTS } from "../constants";

export type CtImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  intent?: ThemeIntent;
  fontVariant?: ThemeFontVariant;
  theme: Theme;
  objectFit: "contain" | "cover" | "fill" | "none" | "scale-down";
  width?: number | string;
  height?: number | string;
  description?: string;
  actionLabel?: string;
  onActionClick?: () => void;
  square?: boolean;
  disablePaddings?: boolean;
  disableBorder?: boolean;
  borderNotCentered?: boolean;
  onComputedDimensionsChange?: (width: number, height: number) => void;
  rounded?: boolean;
};

const bem = new BemHelper("ct-image");

function CtImage({
  intent,
  fontVariant,
  theme,
  objectFit,
  className,
  width,
  height,
  description,
  actionLabel,
  onActionClick,
  square,
  disablePaddings,
  disableBorder,
  borderNotCentered,
  onComputedDimensionsChange,
  rounded,
  ...props
}: CtImageProps) {
  const hasOverlay = description || actionLabel;
  const [computedWidth, setComputedWidth] = useState<number | string | undefined>(width);
  const [computedHeight, setComputedHeight] = useState<number | string | undefined>(height);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (square && (!width || width === "auto" || width === "100%") && ref.current) {
      setComputedWidth(ref.current.offsetWidth);
      setComputedHeight(ref.current.offsetWidth);
      onComputedDimensionsChange?.(ref.current.offsetWidth, ref.current.offsetWidth);
    } else if (square) {
      setComputedWidth(width);
      setComputedHeight(width);
      onComputedDimensionsChange?.(width as number, width as number);
    } else {
      setComputedWidth(width);
      setComputedHeight(height);
      onComputedDimensionsChange?.(width as number, height as number);
    }
  }, [ref, width, square]);

  return (
    <div
      ref={ref}
      className={classNames(
        bem.block(),
        bem.modifier(intent || THEME_INTENTS.DEFAULT),
        bem.modifier(fontVariant || THEME_FONT_VARIANTS.FONT_VARIANT_1),
        bem.modifier(theme),
        bem.modifier(objectFit),
        disablePaddings && bem.modifier("disable-paddings"),
        borderNotCentered && bem.modifier("border-not-centered"),
        rounded && bem.modifier("rounded"),
        className,
      )}
      style={{ width: computedWidth, height: computedHeight }}
      onClick={!actionLabel ? onActionClick : undefined}
    >
      {hasOverlay && (
        <div className={classNames(bem.element("overlay"))}>
          {description && (
            <div className={classNames(bem.element("description"))}>{description}</div>
          )}
          {actionLabel && (
            <CtButton fontVariant={fontVariant} onClick={onActionClick}>
              {actionLabel}
            </CtButton>
          )}
        </div>
      )}
      {!disableBorder && (
        <div
          className={classNames(
            bem.element("border"),
            borderNotCentered && bem.modifier("border-not-centered", "border"),
          )}
        />
      )}
      <img className={classNames(bem.element("img"))} style={{ objectFit }} {...props} />
    </div>
  );
}

CtImage.defaultProps = {
  intent: THEME_INTENTS.DEFAULT,
  fontVariant: THEME_FONT_VARIANTS.FONT_VARIANT_1,
  theme: THEMES.THEME_1,
  objectFit: "cover",
  width: "100%",
  height: "auto",
  square: false,
  disablePaddings: false,
  disableBorder: false,
  borderNotCentered: false,
  onComputedDimensionsChange: () => {},
  rounded: false,
};

export default CtImage;
